import React, { useEffect, useRef, useState } from "react";
import { IconSizeEnum } from "../../../../../types/AppTypes";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { BorderedContainer } from "../../../../../components/BorderedContainer/BorderedContainer";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../../components/core/Select/Select";
import { ReactComponent as ArrowCircleRightIcon } from "../../../../../icons/arrowCircleRight.svg";
import { ReactComponent as FlagIcon } from "../../../../../icons/flag.svg";
import {
  StyledRowContainer,
  StyledBodyInternalGrade,
} from "./InternalGrade.style";
import {
  ASSIGN_LABEL,
  EMPTY_INTERNAL_GRADE_MESSAGE,
  EMPTY_STRING,
  END_LABEL,
  FIELD_IS_REQUIRED_LABEL,
  GRADE_LABEL,
  JOB_FAMILY_LABEL,
  PATH_LABEL,
  RESET_DEVELOPMENT_INDICATORS_CONFIRM_LABEL,
  RESET_DEVELOPMENT_INDICATORS_CONFIRM_MESSAGE,
  START_LABEL,
  SUBGRADE_LABEL,
} from "../../../../../constants/Dictionary";
import { CmSheetFooter } from "../Components/CmSheetFooter/CmSheetFooter";
import { useAppSelector } from "../../../../../hooks/store.hook";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { FormField } from "../../../../../components/core/FormField/FormField";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { UpdateCollaboratorCmSheetInternalGradeRequest } from "../../../../../types/api/collaborators.type";
import { TextBox } from "../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { ReactComponent as UserCheckIcon } from "../../../../../icons/usersCheck.svg";
import internalGradeImage from "../../../../../images/internalGrade.png";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import { PopupConfirm } from "../../../../../components/PopupConfirm/PopupConfirm";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { StyledHideOrShowContainer } from "../SchedaCM.styled";
import { CmSheetSectionProps } from "../SchedaCM";
import {
  useCmSheetInternalGrade,
  useUpdateCmSheetInternalGrade,
} from "../../../../../hooks/cmSheet/internalGrade.hook";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import {
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import { currentStateIsMoreEqualThanTargetState } from "../../../../../utils/cmSheet.utils";

type FormData = {
  start: {
    jobFamilyListId?: number[];
    gradeId?: number | null;
    pathGradeId?: number | null;
    subgradeId?: number | null;
  };
  end: {
    jobFamilyListId?: number[];
    gradeId?: number | null;
    pathGradeId?: number | null;
    subgradeId?: number | null;
  };
};

export const InternalGrade = ({
  footer,
  cmSheetInfo,
  cmSheetStateMachineInfo,
  showFooter,
  isMyProfile,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const { config } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);

  const [hasData, setHasData] = useState<boolean | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const pendingSaveDataRef = useRef<FormData | null>(null);

  const selectedYearConfig = config.find(
    (item) => item.year === cmSheetInfo.configYear,
  )?.config;

  const { data, isFetching } = useCmSheetInternalGrade(cmSheetInfo.id);
  const { updateInternalGrade, isUpdating: updating } =
    useUpdateCmSheetInternalGrade(cmSheetInfo.id);

  const [startPathOptions, setStartPathOptions] = useState<
    SelectOption<number>[]
  >([]);
  const [endPathOptions, setEndPathOptions] = useState<SelectOption<number>[]>(
    [],
  );

  const schema = yup
    .object({
      start: yup.object({
        jobFamilyId: yup.array().of(yup.number()),
        gradeId: yup.number().nullable(),
        pathGradeId: yup
          .number()
          .nullable()
          .when(["gradeId"], {
            is: (gradeId: string) => getPathOptions(Number(gradeId)).length > 0,
            then: (field) => field.required(FIELD_IS_REQUIRED_LABEL),
          }),
        subgradeId: yup
          .number()
          .nullable()
          .when(["gradeId"], {
            is: (gradeId: string) => getPathOptions(Number(gradeId)).length > 0,
            then: (field) => field.required(FIELD_IS_REQUIRED_LABEL),
          }),
      }),
      end: yup.object({
        jobFamilyId: yup.array().of(yup.number()),
        gradeId: yup.number().nullable(),
        pathGradeId: yup
          .number()
          .nullable()
          .when(["gradeId"], {
            is: (gradeId: string) => getPathOptions(Number(gradeId)).length > 0,
            then: (field) => field.required(FIELD_IS_REQUIRED_LABEL),
          }),
        subgradeId: yup
          .number()
          .nullable()
          .when(["gradeId"], {
            is: (gradeId: string) => getPathOptions(Number(gradeId)).length > 0,
            then: (field) => field.required(FIELD_IS_REQUIRED_LABEL),
          }),
      }),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const startGradeId = watch("start.gradeId");
  const startPathGradeId = watch("start.pathGradeId");
  const startSubgradeId = watch("start.subgradeId");
  const endPathGradeId = watch("end.pathGradeId");

  const isEndDisabled = () => {
    if (startGradeId == null) return true;

    const hasPathOptions = getPathOptions(startGradeId).length > 0;
    if (
      hasPathOptions &&
      (startPathGradeId == null || startSubgradeId == null)
    ) {
      return true;
    }

    return false;
  };

  const endGradeOptions = selectedYearConfig
    ? (() => {
        const startGrade = selectedYearConfig.grades.find(
          (g) => g.id === Number(startGradeId),
        );
        if (!startGrade) return selectedYearConfig.grades;
        return selectedYearConfig.grades.filter(
          (g) => g.position >= startGrade.position,
        );
      })()
    : [];

  const subgradeOptions = selectedYearConfig
    ? (() => {
        if (startPathGradeId != null && startSubgradeId != null) {
          if (startPathGradeId === endPathGradeId) {
            const subgrade = selectedYearConfig.subgrades.find(
              (s) => s.id === Number(startSubgradeId),
            );
            if (!subgrade) return selectedYearConfig.subgrades;
            return selectedYearConfig.subgrades.filter(
              (s) => s.position >= subgrade.position,
            );
          }
        }
        return selectedYearConfig.subgrades;
      })()
    : [];

  const resetEnd = () => {
    setValue("end.gradeId", null);
    setValue("end.pathGradeId", null);
    setValue("end.subgradeId", null);
    setEndPathOptions([]);
  };

  const isEmpty = (obj: Record<string, unknown> | null): boolean => {
    if (!obj) {
      return true;
    }

    return Object.values(obj).every(
      (value) => value === null || (Array.isArray(value) && value.length === 0),
    );
  };

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (isFetching || !data) {
      return;
    }

    reset({
      start: {
        jobFamilyListId: data.start?.jobFamilyListId ?? [],
        gradeId: data.start?.gradeId != null ? data.start.gradeId : null,
        pathGradeId:
          data.start?.pathGradeId != null ? data.start.pathGradeId : null,
        subgradeId:
          data.start?.subgradeId != null ? data.start.subgradeId : null,
      },
      end: {
        jobFamilyListId: data.end?.jobFamilyListId ?? [],
        gradeId: data.end?.gradeId != null ? data.end.gradeId : null,
        pathGradeId:
          data.end?.pathGradeId != null ? data.end.pathGradeId : null,
        subgradeId: data.end?.subgradeId != null ? data.end.subgradeId : null,
      },
    });

    setStartPathOptions(
      data.start?.gradeId != null ? getPathOptions(data.start.gradeId) : [],
    );
    setEndPathOptions(
      data.end?.gradeId != null ? getPathOptions(data.end.gradeId) : [],
    );

    if (hasData === null) {
      const bothEmpty: boolean = isEmpty(data.start) && isEmpty(data.end);
      setHasData(!bothEmpty);
    }
  }, [data, hasData, isFetching, reset, selectedYearConfig, edit]);

  const handleSetEdit = (value: boolean) => {
    setEdit(value);
    setStartPathOptions([]);
    setEndPathOptions([]);
  };

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  const showLoader = isFetching && hasData === null;
  const showEmptyState = !isFetching && !hasData && !edit;
  const showPage = hasData === true || edit;

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="cmInternalGradeForm"
        disabledEditButton={!isDirty || updating || isFetching}
        edit={edit}
        showEdit={canEdit() && (!!hasData || edit)}
        setEdit={handleSetEdit}
        showFooter={showFooter}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isFetching,
    updating,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  if (!selectedYearConfig) {
    return null;
  }

  const getPathIdByPathGradeId = (pathGradeId: number) => {
    const pathGrade = selectedYearConfig.pathGrades.find(
      (item) => item.id === pathGradeId,
    );

    if (pathGrade) {
      const path = selectedYearConfig.paths.find(
        (item) => item.id === pathGrade.pathId,
      );

      if (path) {
        return path.id;
      }
    }

    return null;
  };

  const saveInternalGrade = async (formData: FormData) => {
    try {
      const body: UpdateCollaboratorCmSheetInternalGradeRequest = {
        start: {
          jobFamilyListId: formData.start.jobFamilyListId ?? [],
          gradeId:
            formData.start.gradeId !== null
              ? Number(formData.start.gradeId)
              : null,
          pathId:
            formData.start.pathGradeId !== null
              ? getPathIdByPathGradeId(Number(formData.start.pathGradeId))
              : null,
          pathGradeId:
            formData.start.pathGradeId !== null
              ? Number(formData.start.pathGradeId)
              : null,
          subgradeId:
            formData.start.subgradeId !== null
              ? Number(formData.start.subgradeId)
              : null,
        },
        end: {
          jobFamilyListId: formData.end.jobFamilyListId ?? [],
          gradeId:
            formData.end.gradeId !== null ? Number(formData.end.gradeId) : null,
          pathId:
            formData.end.pathGradeId !== null
              ? getPathIdByPathGradeId(Number(formData.end.pathGradeId))
              : null,
          pathGradeId:
            formData.end.pathGradeId !== null
              ? Number(formData.end.pathGradeId)
              : null,
          subgradeId:
            formData.end.subgradeId !== null
              ? Number(formData.end.subgradeId)
              : null,
        },
      };
      await updateInternalGrade(body);
    } finally {
      pendingSaveDataRef.current = null;
      setShowSaveConfirm(false);
      setHasData(null);
      setEdit(false);
    }
  };

  const startGradeIsDisabled = () => {
    if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
      return false;
    }

    if (
      roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
      profile.id === cmSheetInfo.careerManagerId &&
      currentStateIsMoreEqualThanTargetState(
        cmSheetStateMachineInfo.currentState,
        CmSheetStateEnum.INTERMEDIATE_EVALUATION,
      )
    ) {
      return true;
    }

    return false;
  };

  const onSubmit = (formData: FormData) => {
    if (
      roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) &&
      dirtyFields.start?.gradeId
    ) {
      pendingSaveDataRef.current = formData;
      setShowSaveConfirm(true);
      return;
    }

    return saveInternalGrade(formData);
  };

  const handleConfirmSave = () => {
    if (pendingSaveDataRef.current) {
      return saveInternalGrade(pendingSaveDataRef.current);
    }
  };

  const getPathOptions = (selectedGradeId?: number | null) => {
    if (selectedGradeId == null) {
      return [];
    }

    let options: SelectOption<number>[] = [];
    const grade = selectedYearConfig.grades.find(
      (grade) => grade.id === Number(selectedGradeId),
    );

    if (grade) {
      const pathGrades = selectedYearConfig.pathGrades.filter(
        (pathGrade) => pathGrade.gradeId === grade.id,
      );
      pathGrades.forEach((pathGrade) => {
        const path = selectedYearConfig.paths.find(
          (path) => pathGrade.pathId === path.id,
        );

        if (path) {
          options.push({
            label: `${path.name} - ${pathGrade.name}`,
            value: pathGrade.id,
          });
        }
      });
    }
    return options;
  };

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState
            label={canEdit() ? EMPTY_INTERNAL_GRADE_MESSAGE : ""}
            imageSrc={internalGradeImage}
          >
            {canEdit() && (
              <Button
                label={ASSIGN_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<UserCheckIcon />}
                onClick={() => (setEdit ? setEdit(true) : {})}
              />
            )}
          </EmptyState>
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        <Loader loading={updating}>
          <StyledBodyInternalGrade>
            <form id="cmInternalGradeForm" onSubmit={handleSubmit(onSubmit)}>
              <div className="internal-grade-bordered-container">
                <BorderedContainer
                  label={START_LABEL}
                  icon={
                    <IconManager size={IconSizeEnum.SMALL}>
                      <ArrowCircleRightIcon />
                    </IconManager>
                  }
                >
                  <StyledHideOrShowContainer $show={edit}>
                    <StyledRowContainer>
                      <FormField error={errors.start?.jobFamilyListId?.message}>
                        <Controller
                          name="start.jobFamilyListId"
                          control={control}
                          render={({ field }) => (
                            <Select
                              variant={SelectVariantEnum.MULTI_SELECT}
                              label={JOB_FAMILY_LABEL}
                              options={selectedYearConfig.jobFamilies.map(
                                (item) => ({
                                  label: item.name,
                                  value: item.id,
                                }),
                              )}
                              {...field}
                              maxSelections={2}
                            />
                          )}
                        />
                      </FormField>
                      <FormField error={errors.start?.gradeId?.message}>
                        <Controller
                          name="start.gradeId"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Select
                              variant={SelectVariantEnum.SINGLE_SELECT}
                              label={GRADE_LABEL}
                              options={selectedYearConfig.grades.map(
                                (item) => ({
                                  label: item.name,
                                  value: item.id,
                                }),
                              )}
                              value={value}
                              onChange={(value) => {
                                const options = getPathOptions(value);
                                if (value != null && options.length > 0) {
                                  setStartPathOptions(getPathOptions(value));
                                } else {
                                  setStartPathOptions([]);
                                }
                                setValue("start.pathGradeId", null);
                                setValue("start.subgradeId", null);
                                resetEnd();
                                onChange(value);
                              }}
                              disabled={startGradeIsDisabled()}
                            />
                          )}
                        />
                      </FormField>
                      <StyledHideOrShowContainer
                        $show={startPathOptions.length > 0}
                      >
                        <FormField error={errors.start?.pathGradeId?.message}>
                          <Controller
                            name="start.pathGradeId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                variant={SelectVariantEnum.SINGLE_SELECT}
                                label={PATH_LABEL}
                                options={startPathOptions}
                                {...field}
                              />
                            )}
                          />
                        </FormField>
                      </StyledHideOrShowContainer>
                      <StyledHideOrShowContainer
                        $show={startPathOptions.length > 0}
                      >
                        <FormField error={errors.start?.subgradeId?.message}>
                          <Controller
                            name="start.subgradeId"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                variant={SelectVariantEnum.SINGLE_SELECT}
                                label={SUBGRADE_LABEL}
                                options={selectedYearConfig.subgrades.map(
                                  (item) => ({
                                    label: item.name,
                                    value: item.id,
                                  }),
                                )}
                                value={value}
                                onChange={(value) => {
                                  onChange(value);
                                  setValue("end.subgradeId", null);
                                }}
                              />
                            )}
                          />
                        </FormField>
                      </StyledHideOrShowContainer>
                      <StyledHideOrShowContainer
                        $show={startPathOptions.length === 0}
                      >
                        <TextBox label={PATH_LABEL} text={EMPTY_STRING} />
                      </StyledHideOrShowContainer>
                      <StyledHideOrShowContainer
                        $show={startPathOptions.length === 0}
                      >
                        <TextBox label={SUBGRADE_LABEL} text={EMPTY_STRING} />
                      </StyledHideOrShowContainer>
                    </StyledRowContainer>
                  </StyledHideOrShowContainer>
                  <StyledHideOrShowContainer $show={!edit}>
                    <StyledRowContainer>
                      <TextBox
                        label={JOB_FAMILY_LABEL}
                        text={selectedYearConfig.jobFamilies
                          .filter((item) =>
                            data?.start?.jobFamilyListId.includes(item.id),
                          )
                          .map((item) => item.name)
                          .join(", ")}
                      />
                      <TextBox
                        label={GRADE_LABEL}
                        text={
                          data?.start?.gradeId != null
                            ? selectedYearConfig.grades
                                .filter(
                                  (item) => data?.start?.gradeId === item.id,
                                )
                                .map((item) => item.name)
                            : null
                        }
                      />
                      <TextBox
                        label={PATH_LABEL}
                        text={`${selectedYearConfig.paths
                          .filter((item) => data?.start?.pathId === item.id)
                          .map(
                            (item) => item.name,
                          )} - ${selectedYearConfig.pathGrades
                          .filter(
                            (item) => data?.start?.pathGradeId === item.id,
                          )
                          .map((item) => item.name)}`}
                      />
                      <TextBox
                        label={SUBGRADE_LABEL}
                        text={
                          data?.start?.subgradeId != null
                            ? selectedYearConfig.subgrades
                                .filter(
                                  (item) => data?.start?.subgradeId === item.id,
                                )
                                .map((item) => item.name)
                            : null
                        }
                      />
                    </StyledRowContainer>
                  </StyledHideOrShowContainer>
                </BorderedContainer>
              </div>
              <div className="internal-grade-bordered-container">
                <BorderedContainer
                  label={END_LABEL}
                  icon={
                    <IconManager size={IconSizeEnum.SMALL}>
                      <FlagIcon />
                    </IconManager>
                  }
                >
                  <StyledHideOrShowContainer $show={edit}>
                    <StyledRowContainer>
                      <FormField error={errors.end?.jobFamilyListId?.message}>
                        <Controller
                          name="end.jobFamilyListId"
                          control={control}
                          render={({ field }) => (
                            <Select
                              variant={SelectVariantEnum.MULTI_SELECT}
                              label={JOB_FAMILY_LABEL}
                              options={selectedYearConfig.jobFamilies.map(
                                (item) => ({
                                  label: item.name,
                                  value: item.id,
                                }),
                              )}
                              {...field}
                              maxSelections={2}
                            />
                          )}
                        />
                      </FormField>
                      <FormField error={errors.end?.gradeId?.message}>
                        <Controller
                          name="end.gradeId"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Select
                              variant={SelectVariantEnum.SINGLE_SELECT}
                              label={GRADE_LABEL}
                              options={endGradeOptions.map((item) => ({
                                label: item.name,
                                value: item.id,
                              }))}
                              disabled={isEndDisabled()}
                              value={value}
                              onChange={(value) => {
                                const options = getPathOptions(value);
                                if (value != null && options.length > 0) {
                                  setEndPathOptions(getPathOptions(value));
                                } else {
                                  setEndPathOptions([]);
                                }
                                setValue("end.pathGradeId", null);
                                setValue("end.subgradeId", null);
                                onChange(value);
                              }}
                            />
                          )}
                        />
                      </FormField>
                      <StyledHideOrShowContainer
                        $show={endPathOptions.length > 0}
                      >
                        <FormField error={errors.end?.pathGradeId?.message}>
                          <Controller
                            name="end.pathGradeId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                variant={SelectVariantEnum.SINGLE_SELECT}
                                label={PATH_LABEL}
                                options={endPathOptions}
                                disabled={isEndDisabled()}
                                {...field}
                              />
                            )}
                          />
                        </FormField>
                      </StyledHideOrShowContainer>
                      <StyledHideOrShowContainer
                        $show={endPathOptions.length > 0}
                      >
                        <FormField error={errors.end?.subgradeId?.message}>
                          <Controller
                            name="end.subgradeId"
                            control={control}
                            render={({ field }) => (
                              <Select
                                variant={SelectVariantEnum.SINGLE_SELECT}
                                label={SUBGRADE_LABEL}
                                options={subgradeOptions.map((item) => ({
                                  label: item.name,
                                  value: item.id,
                                }))}
                                disabled={isEndDisabled()}
                                {...field}
                              />
                            )}
                          />
                        </FormField>
                      </StyledHideOrShowContainer>
                      <StyledHideOrShowContainer
                        $show={endPathOptions.length === 0}
                      >
                        <TextBox label={PATH_LABEL} text={EMPTY_STRING} />
                      </StyledHideOrShowContainer>
                      <StyledHideOrShowContainer
                        $show={endPathOptions.length === 0}
                      >
                        <TextBox label={SUBGRADE_LABEL} text={EMPTY_STRING} />
                      </StyledHideOrShowContainer>
                    </StyledRowContainer>
                  </StyledHideOrShowContainer>
                  <StyledHideOrShowContainer $show={!edit}>
                    <StyledRowContainer>
                      <TextBox
                        label={JOB_FAMILY_LABEL}
                        text={selectedYearConfig.jobFamilies
                          .filter((item) =>
                            data?.end?.jobFamilyListId.includes(item.id),
                          )
                          .map((item) => item.name)
                          .join(", ")}
                      />
                      <TextBox
                        label={GRADE_LABEL}
                        text={
                          data?.end?.gradeId != null
                            ? selectedYearConfig.grades
                                .filter(
                                  (item) => data?.end?.gradeId === item.id,
                                )
                                .map((item) => item.name)
                            : null
                        }
                      />
                      <TextBox
                        label={PATH_LABEL}
                        text={`${selectedYearConfig.paths
                          .filter((item) => data?.end?.pathId === item.id)
                          .map(
                            (item) => item.name,
                          )} - ${selectedYearConfig.pathGrades
                          .filter((item) => data?.end?.pathGradeId === item.id)
                          .map((item) => item.name)}`}
                      />
                      <TextBox
                        label={SUBGRADE_LABEL}
                        text={
                          data?.end?.subgradeId != null
                            ? selectedYearConfig.subgrades
                                .filter(
                                  (item) => data?.end?.subgradeId === item.id,
                                )
                                .map((item) => item.name)
                            : null
                        }
                      />
                    </StyledRowContainer>
                  </StyledHideOrShowContainer>
                </BorderedContainer>
              </div>
            </form>
          </StyledBodyInternalGrade>
        </Loader>
      </StyledHideOrShowContainer>
      <PopupConfirm
        isOpen={showSaveConfirm}
        label={RESET_DEVELOPMENT_INDICATORS_CONFIRM_LABEL}
        popupText={RESET_DEVELOPMENT_INDICATORS_CONFIRM_MESSAGE}
        onConfirm={handleConfirmSave}
        onCancel={() => {
          pendingSaveDataRef.current = null;
          setShowSaveConfirm(false);
        }}
        isLoading={updating}
      />
    </>
  );
};
